import { defineMessages, IntlShape } from '@redbubble/next/intl';
import type {
  LineItem,
  LineItemKind,
  PageType,
  PayPalRequiredCart,
  ShippingOption,
  ShippingOptionType,
} from '../types';

const STANDARD_SHIPPING_KEY = 'standard';
const EXPRESS_SHIPPING_KEY = 'express';

const messages = defineMessages({
  productSummary: {
    defaultMessage: 'Cart of {cartLineItemsCount} item(s)',
  },
  coupon: {
    defaultMessage: 'Coupon',
  },
  giftCard: {
    defaultMessage: 'Gift Card',
  },
  [STANDARD_SHIPPING_KEY]: {
    defaultMessage: 'Standard Shipping',
  },
  [EXPRESS_SHIPPING_KEY]: {
    defaultMessage: 'Express Shipping',
  },
});

const applicableCoupon = (
  coupon: PayPalRequiredCart['applied_coupon'],
  intl: IntlShape
): { displayName: string; discountAmount: number } | null => {
  if (!coupon) return null;

  const amount = Number(coupon.amount);

  if (coupon.type === 'AmountCoupon' && amount > 0) {
    return {
      displayName: intl.formatMessage(messages.coupon),
      discountAmount: amount,
    };
  }

  if (coupon.type === 'GiftCertificateCoupon' && amount > 0) {
    return {
      displayName: intl.formatMessage(messages.giftCard),
      discountAmount: amount,
    };
  }

  return null;
};

const createCouponLineItem = (
  coupon: PayPalRequiredCart['applied_coupon'],
  intl: IntlShape
): LineItem | null => {
  const couponInfo = applicableCoupon(coupon, intl);

  if (!couponInfo) return null;

  return {
    quantity: '1',
    unitAmount: couponInfo.discountAmount.toString(),
    name: couponInfo.displayName,
    kind: 'credit' as LineItemKind,
    unitTaxAmount: undefined,
    description: undefined,
    productCode: undefined,
    url: undefined,
  };
};

const createProductLineItem = ({
  intl,
  cartLineItems,
  subtotal,
  tax,
  showTaxSeparately,
  estimatedShipping,
  isCreateOrder,
  page,
}: {
  intl: IntlShape;
  cartLineItems: PayPalRequiredCart['items'];
  subtotal: PayPalRequiredCart['summary']['subtotal'];
  tax: PayPalRequiredCart['summary']['tax'];
  showTaxSeparately: boolean;
  estimatedShipping: PayPalRequiredCart['summary']['estimated_shipping'];
  isCreateOrder: boolean;
  page: PageType;
}): LineItem => {
  const cartLineItemsCount = cartLineItems.reduce(
    (total, { quantity }) => total + quantity,
    0
  );

  const shippingPreference = estimatedShipping.preference || 'standard';
  const shippingAmountWithTax = estimatedShipping[shippingPreference] || 0;
  const amountExcludingTax =
    estimatedShipping[`${shippingPreference}_ex_tax`] || 0;
  const shippingTax = shippingAmountWithTax - amountExcludingTax;

  // As shipping tax is included in the shipping options, we subtract
  // it from the order tax total, giving us the product tax total.
  const productTax = (tax?.amount || 0) - shippingTax;

  // Sometimes when we are starting a PayPal transaction (handleCreateOrder) the cart can contain tax when showTaxSeparately = true.
  // It can happen when an attempt to refetch a tax-free cart fails and the existing "with taxes" cart is in our React state.
  // If that is the case, we need to deduct product tax from the unit amount as PayPal expects "unitAmount" to be tax-free if
  // we provide it with a non-zero "unitTaxAmount". PayPal will error with "invalid unit amount" if we do not do this.
  // We only need to do this for the cart page because the cart BFF sets "subtotal" based on whether we are showing tax
  // separately or not - see "transformSummary()" in "app/types/cart-bff/utils.ts".
  const unitAmount = (
    showTaxSeparately && isCreateOrder && page === 'cart'
      ? subtotal - productTax
      : subtotal
  ).toFixed(2);

  // We show tax separately for some jurisdictions so we show tax in the modal in those cases.
  // PayPal does not include this value in its validation of order totals.
  const unitTaxAmount =
    showTaxSeparately && productTax > 0 ? productTax.toFixed(2) : undefined;

  return {
    name: intl.formatMessage(messages.productSummary, {
      cartLineItemsCount,
    }),
    quantity: '1',
    unitAmount,
    unitTaxAmount,
    kind: 'debit' as LineItemKind,
    description: undefined,
    productCode: undefined,
    url: undefined,
  };
};

const createPaypalShippingLineItem = (
  estimatedShipping: PayPalRequiredCart['summary']['estimated_shipping'],
  showTaxSeparately: boolean,
  intl: IntlShape
): LineItem => {
  const shippingPreference = estimatedShipping.preference || 'standard';
  const shippingAmountWithTax = estimatedShipping[shippingPreference] || 0;
  const shippingAmountExcludingTax =
    estimatedShipping[`${shippingPreference}_ex_tax`] || 0;
  const shippingTax = shippingAmountWithTax - shippingAmountExcludingTax;

  const isKnownShippingMethod = [
    STANDARD_SHIPPING_KEY,
    EXPRESS_SHIPPING_KEY,
  ].find((s) => s === shippingPreference);

  const lineItemName = isKnownShippingMethod
    ? intl.formatMessage(messages[shippingPreference])
    : shippingPreference;

  return {
    quantity: '1',
    unitAmount: showTaxSeparately
      ? shippingAmountExcludingTax.toString()
      : shippingAmountWithTax.toString(),
    unitTaxAmount: showTaxSeparately ? shippingTax.toFixed(2) : undefined,
    name: lineItemName,
    kind: 'debit' as LineItemKind,
    description: undefined,
    productCode: undefined,
    url: undefined,
  };
};

export const toPaypalLineItems = ({
  cartLineItems,
  cartSummary,
  cartCoupon,
  showShippingMethodAsLineItem,
  intl,
  isCreateOrder,
  page,
}: {
  cartLineItems: PayPalRequiredCart['items'];
  cartSummary: PayPalRequiredCart['summary'];
  cartCoupon: PayPalRequiredCart['applied_coupon'];
  showShippingMethodAsLineItem: boolean;
  intl: IntlShape;
  isCreateOrder: boolean;
  page: PageType;
}) => {
  const {
    subtotal,
    tax,
    show_tax_separately: showTaxSeparately,
    estimated_shipping: estimatedShipping,
  } = cartSummary;
  const lineItem = [
    createProductLineItem({
      intl,
      cartLineItems,
      subtotal,
      tax,
      showTaxSeparately,
      estimatedShipping,
      isCreateOrder,
      page,
    }),
  ];

  const couponLineItem = createCouponLineItem(cartCoupon, intl);
  if (couponLineItem) {
    lineItem.push(couponLineItem);
  }

  if (showShippingMethodAsLineItem) {
    lineItem.push(
      createPaypalShippingLineItem(estimatedShipping, showTaxSeparately, intl)
    );
  }

  return lineItem;
};

export const toPaypalShippingOptions = ({
  estimatedShipping,
  currency,
  intl,
}: {
  estimatedShipping: PayPalRequiredCart['summary']['estimated_shipping'];
  currency: string;
  intl: IntlShape;
}) => {
  const shippingPreference = estimatedShipping.preference || 'standard';

  return (
    [STANDARD_SHIPPING_KEY, EXPRESS_SHIPPING_KEY]
      .map((key) => {
        const shippingAmountWithTax = estimatedShipping[key];

        /* Express Shipping can be null (e.g. cart's line items only has gift certs)
         * Standard Shipping cannot be null
         */
        if (shippingAmountWithTax === null) return null;
        const isSelected =
          key.toLocaleLowerCase() === shippingPreference.toLocaleLowerCase();

        const paypalShippingOption: ShippingOption = {
          // aligns with "ShippingMethod" type in cart.graphql.ts
          id: key.toUpperCase(),
          label: intl.formatMessage(messages[key]),
          selected: isSelected,
          amount: {
            value: shippingAmountWithTax.toString(),
            currency,
          },
          type: 'SHIPPING' as ShippingOptionType,
        };

        return paypalShippingOption;
      })
      // Even though we've removed any possible null elements with the filter(Boolean),
      // Typescript still thinks that there could be null elements. This additional
      // Type assignment is to ensure that the editor knows that the return type
      // is ShippingOption[], not (ShippingOption | null)[]
      .filter(Boolean) as ShippingOption[]
  );
};

export const subtotalAfterCouponApplied = ({
  subtotal,
  coupon: appliedCoupon,
  intl,
}: {
  subtotal: number;
  coupon: PayPalRequiredCart['applied_coupon'];
  intl: IntlShape;
}) => {
  const coupon = applicableCoupon(appliedCoupon, intl);
  if (!coupon) return subtotal;

  return Math.max(subtotal - coupon.discountAmount, 0);
};
